import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@primitives/form";
import { Button } from "@primitives/button";
import { Input } from "@primitives/input";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import { ApiError } from "@/server/apiError";
import { sendVerificationCode } from "@/server/services/authService";
import { EmailVerificationSchema, EmailVerificationType } from "@/schemas/auth";
import { useNavigate } from "react-router-dom";

interface Props {
  expired?: boolean;
  onSuccess: (email: string) => void;
  onSkip: (email: string) => void;
}

export default function EmailVerificationStep({
  expired,
  onSuccess,
  onSkip,
}: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<EmailVerificationType>({
    resolver: zodResolver(EmailVerificationSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit({ email }: EmailVerificationType) {
    try {
      await sendVerificationCode(email);
      onSuccess(email);
    } catch (error) {
      if (error instanceof ApiError) {
        const code = error.data?.title;

        if (code === "errors.EmailVerification.NotEnabled") {
          onSkip(email);
          return;
        }

        if (code === "errors.EmailVerification.EmailAlreadyRegistered") {
          form.setError("email", {
            message: t("errors.EmailVerification.EmailAlreadyRegistered"),
          });
          return;
        }

        toast({
          // Coalesce before building the key array — i18next throws on an
          // undefined key entry rather than falling through to the fallback.
          description: t([code ?? "errorOccurred", "errorOccurred"]),
          variant: "destructive",
        });
      } else {
        toast({
          description:
            error instanceof Error ? error.message : t("errorOccurred"),
          variant: "destructive",
        });
      }
    }
  }

  return (
    <div className="my-7 space-y-4">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">
          {t("register.emailStep.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {expired
            ? t("register.emailStep.codeExpired")
            : t("register.emailStep.description")}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="rounded-lg border-border bg-card py-6 placeholder:opacity-70"
                    placeholder={t("register.emailStep.emailPlaceholder")}
                    autoComplete="email"
                    type="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={form.formState.isSubmitting}
            className="h-12 w-full rounded-lg"
          >
            {form.formState.isSubmitting
              ? t("register.emailStep.sending")
              : t("register.emailStep.continueButton")}
          </Button>
        </form>
      </Form>

      <p className="text-center text-sm text-muted-foreground">
        {t("register.emailStep.alreadyHaveAccount")}{" "}
        <button
          type="button"
          className="text-secondary underline"
          onClick={() => navigate("/login")}
        >
          {t("login.title")}
        </button>
      </p>
    </div>
  );
}
